// API types for the AI Survey Simulation

export type ModelType = "gpt-5" | "gpt-5-nano";

export type ReasoningEffort = "none" | "low" | "medium" | "high";

export interface FewShotExample {
  profile_summary: string;
  reasoning?: string;
  answer: "si" | "no";
}

export interface SimulationRequest {
  sample_size: number;
  selected_variables: string[];
  custom_prompt?: string;
  target_variable: string;
  model?: ModelType;
  reasoning_effort?: ReasoningEffort;
  enable_chain_of_thought?: boolean;
  few_shot_examples?: FewShotExample[];
  max_concurrency?: number;
}

export interface TargetVariable {
  name: string;
  label: string;
  category: string;
  valid_count: number;
  description: string;
}

export interface AIResponse {
  persona_id: number;
  full_response: string;
  classified_answer: string;
  ground_truth?: string;
  reasoning?: string;
  persona_profile?: string;
}

export interface StreamingUpdate {
  type: "progress" | "persona_complete" | "error" | "complete";
  persona_id?: number;
  total_personas?: number;
  response?: AIResponse;  // For persona_complete events
  persona_profile?: string;  // For persona_complete events - the input profile
  partial_text?: string;
  error_message?: string;
  results?: SimulationResults;  // For complete events
  message?: string;  // For progress/error messages
}

export interface SimulationResults {
  total_responses: number;
  si_count: number;
  no_count: number;
  other_count: number;
  accuracy?: number;
  precision_si?: number;
  recall_si?: number;
  precision_no?: number;
  recall_no?: number;
  f1_si?: number;
  f1_no?: number;
  sample_responses: AIResponse[];
  ground_truth_distribution?: { [key: string]: number };
  confusion_matrix?: {
    tp: number;  // True Positives
    fp: number;  // False Positives
    tn: number;  // True Negatives
    fn: number;  // False Negatives
  };
}

export interface VariableInfo {
  name: string;
  label: string;
  category?: string;
  value_labels?: { [key: string]: string };
}

export interface DatasetInfo {
  total_respondents: number;
  total_variables: number;
  available_variables: Array<{ name: string; label: string }>;
  target_variable_info: {
    name: string;
    label: string;
    distribution: { [key: string]: number };
  };
}

export interface PersonaPreview {
  persona_id: number;
  profile_text: string;
  ground_truth?: string;
}

export interface Experiment {
  id: string;
  name: string;
  timestamp: string;
  parameters: {
    sample_size: number;
    selected_variables: string[];
    custom_prompt?: string;
    target_variable: string;
    model?: ModelType;
    reasoning_effort?: ReasoningEffort;
    enable_chain_of_thought?: boolean;
    few_shot_examples?: FewShotExample[];
  };
  results: SimulationResults;
}

// Model configuration options for the UI
export interface ModelConfig {
  id: ModelType;
  name: string;
  reasoningEffort: ReasoningEffort;
  description: string;
  supportsStreaming: boolean;
}

export const AVAILABLE_MODELS: ModelConfig[] = [
  {
    id: "gpt-5-nano",
    name: "Fastest",
    reasoningEffort: "none",
    description: "Direct responses, no reasoning",
    supportsStreaming: true,
  },
  {
    id: "gpt-5-nano",
    name: "Low",
    reasoningEffort: "low",
    description: "Light reasoning",
    supportsStreaming: true,
  },
  {
    id: "gpt-5",
    name: "Medium",
    reasoningEffort: "medium",
    description: "Balanced reasoning",
    supportsStreaming: true,
  },
  {
    id: "gpt-5",
    name: "High",
    reasoningEffort: "high",
    description: "Deep reasoning",
    supportsStreaming: true,
  },
];
